import type { Category, PlanEvent, Task } from "../../../../shared/types";
import type { HolidayEntry } from "../../../../shared/hooks/use-holidays";
import { CATEGORY_DOT_COLORS } from "../../../../shared/types";
import {
  getDayName,
  getDayNumber,
} from "../../../../shared/types/date-helpers";
import { MonthGrid } from "../MonthGrid";

interface MobileDayStripProps {
  readonly days: readonly string[];
  readonly today: string;
  readonly selectedDate: string | null;
  readonly categoryDotsForDate: ReadonlyMap<string, Set<Category>>;
  readonly eventsByDate: ReadonlyMap<string, PlanEvent[]>;
  readonly tasksByDate: ReadonlyMap<string, Task[]>;
  readonly calendarMode: "week" | "month" | "day";
  readonly onDayClick: (date: string) => void;
  readonly onPrev?: () => void;
  readonly onNext?: () => void;
  readonly holidays?: ReadonlyMap<string, HolidayEntry>;
}

export function MobileDayStrip({
  days,
  today,
  selectedDate,
  categoryDotsForDate,
  eventsByDate,
  tasksByDate,
  calendarMode,
  onDayClick,
  onPrev,
  onNext,
  holidays,
}: MobileDayStripProps) {
  if (calendarMode === "month") {
    return (
      <MonthGrid
        days={days}
        currentDate={today}
        today={today}
        selectedDate={selectedDate}
        eventsByDate={eventsByDate}
        tasksByDate={tasksByDate}
        onDayClick={onDayClick}
        holidays={holidays}
      />
    );
  }

  // Mode jour : flèches de navigation + jour unique
  if (days.length === 1 && onPrev && onNext) {
    const day = days[0];
    const isToday = day === today;
    const cats = categoryDotsForDate.get(day) ?? new Set<Category>();
    const dayEvents = eventsByDate.get(day) ?? [];
    const dayTasks = tasksByDate.get(day) ?? [];
    const hasItems = dayEvents.length > 0 || dayTasks.length > 0;
    return (
      <div className="flex items-center justify-between w-full">
        <button
          type="button"
          onClick={onPrev}
          className="w-9 h-9 flex items-center justify-center rounded-lg text-ink-muted hover:text-ink hover:bg-surface-alt active:bg-surface-alt/70 transition-colors"
          aria-label="Jour précédent"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex flex-col items-center gap-0.5">
          <span className="text-[10px] uppercase text-ink-faint leading-none">{getDayName(day)}</span>
          <span className={`w-9 h-9 flex items-center justify-center rounded-full text-lg leading-none ${isToday ? "bg-accent text-white" : "text-ink"}`}>
            {getDayNumber(day)}
          </span>
          {hasItems && (
            <div className="flex gap-0.5">
              {Array.from(cats).slice(0, 3).map((cat) => (
                <span key={cat} className={`w-1.5 h-1.5 rounded-full ${CATEGORY_DOT_COLORS[cat]}`} />
              ))}
            </div>
          )}
          {holidays?.get(day) && (
            <span
              className={`text-[8px] font-medium leading-tight ${holidays.get(day)!.official ? "text-red-500 dark:text-red-400" : "text-ink-faint"}`}
              title={holidays.get(day)!.official ? `${holidays.get(day)!.name} — Jour férié` : holidays.get(day)!.name}
            >
              {holidays.get(day)!.name}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onNext}
          className="w-9 h-9 flex items-center justify-center rounded-lg text-ink-muted hover:text-ink hover:bg-surface-alt active:bg-surface-alt/70 transition-colors"
          aria-label="Jour suivant"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div
      className="flex gap-0.5 overflow-x-auto scrollbar-none"
      role="grid"
      aria-label="Calendrier"
    >
      {days.map((day) => {
        const isToday = day === today;
        const isSelected = day === selectedDate;
        const dayEvents = eventsByDate.get(day) ?? [];
        const dayTasks = tasksByDate.get(day) ?? [];
        const hasItems = dayEvents.length > 0 || dayTasks.length > 0;
        const cats = categoryDotsForDate.get(day) ?? new Set<Category>();

        return (
          <button
            key={day}
            type="button"
            onClick={() => onDayClick(day)}
            aria-label={`${getDayName(day)} ${getDayNumber(day)}${hasItems ? `, ${dayEvents.length} événement(s)` : ""}`}
            className={`flex flex-col items-center gap-0.5 py-1.5 px-2 min-w-[44px] rounded-lg
              text-center focus:outline-none focus:ring-2 focus:ring-accent/40 active:scale-95 transition-all flex-1
              ${isSelected ? "bg-accent/10" : "hover:bg-surface-alt/50"}
              ${isToday ? "font-bold" : ""}`}
          >
            <span className="text-[10px] uppercase text-ink-faint leading-none">
              {getDayName(day)}
            </span>
            <span
              className={`w-7 h-7 flex items-center justify-center rounded-full text-sm leading-none
                ${isToday ? "bg-accent text-white" : ""}
                ${!isToday && isSelected ? "bg-accent/80 text-white" : ""}
                ${!isToday && !isSelected ? "text-ink" : ""}
              `}
            >
              {getDayNumber(day)}
            </span>
            {hasItems && (
              <div className="flex gap-0.5">
                {Array.from(cats).slice(0, 3).map((cat) => (
                  <span
                    key={cat}
                    className={`w-1.5 h-1.5 rounded-full ${CATEGORY_DOT_COLORS[cat]}`}
                  />
                ))}
              </div>
            )}
            {holidays?.get(day) && (
              <span
                className={`text-[7px] font-medium truncate max-w-full leading-tight ${holidays.get(day)!.official ? "text-red-500 dark:text-red-400" : "text-ink-faint"}`}
                title={holidays.get(day)!.official ? `${holidays.get(day)!.name} — Jour férié` : holidays.get(day)!.name}
              >
                {holidays.get(day)!.name}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
